import React from 'react'
import { match } from 'ts-pattern'
import CognitionChatInputMessage from './cognitionChatInputMessage'
import CognitionChatOutputMessage from './cognitionChatOutputMessage'
import CognitionObjectMessage from './cognitionObjectMessage'
import CognitionProgressMessage from './cognitionProgressMessage'
import type {
  CognitionEvaluationGroupMessage,
  CognitionEvaluationChatInputMessage,
  CognitionEvaluationChatOutputMessage,
  CognitionEvaluationRole,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import type {
  MarkdownComponentAction,
  MarkdownComponentMap,
} from '../elementals/markdownWithComponents/types'
import type { AiChatMode } from '../aiChat'

// Extend the type to include __typename
export interface CognitionMessageProps {
  message: CognitionEvaluationGroupMessage
  index: number
  previousMessageRole?: CognitionEvaluationRole
  isLastMessage?: boolean
  onCopy?: (content: string) => void
  onAction?: (action: MarkdownComponentAction) => void
  components?: MarkdownComponentMap
  mode?: AiChatMode
  isVisible?: boolean
}

/**
 * Check if a message is a progress/interim message.
 * Progress messages have a `description` field and no recognized __typename for chat output.
 */
function isProgressMessage(message: CognitionEvaluationGroupMessage): boolean {
  return 'description' in message && !!(message as any).description
}

/**
 * Check if a message is a private/system message (visibility: 'PRIVATE').
 * Private messages are rendered in the same style as progress messages.
 */
function isPrivateMessage(message: CognitionEvaluationGroupMessage): boolean {
  return 'visibility' in message && (message as any).visibility === 'PRIVATE'
}

const CognitionMessage: React.FC<CognitionMessageProps> = ({
  message,
  index,
  previousMessageRole,
  isLastMessage = false,
  onCopy,
  onAction,
  components,
  // @ts-ignore next -- including this here for future use
  mode = 'chat', // eslint-disable-line
  isVisible = true,
}) => {
  const elmId = 'cognition_message'

  // Show assistant label only if previous message was NOT from the model
  const showAssistantLabel =
    previousMessageRole !==
    (graph.enums.CognitionEvaluationRole
      .Model as unknown as CognitionEvaluationRole)

  // Handle progress messages before the pattern match
  if (isProgressMessage(message)) {
    return (
      <CognitionProgressMessage
        message={message}
        index={index}
        showAssistantLabel={false}
        isActive={isLastMessage}
        isVisible={isVisible}
        elmId={elmId}
      />
    )
  }

  // Handle private messages — render like progress messages
  if (isPrivateMessage(message)) {
    const privateAsProgress = {
      ...message,
      description: (message as any).object?.message || '',
    }
    return (
      <CognitionProgressMessage
        message={privateAsProgress as CognitionEvaluationGroupMessage}
        index={index}
        showAssistantLabel={false}
        isActive={false}
        isVisible={isVisible}
        compact
        elmId={elmId}
      />
    )
  }

  return match(message)
    .with(
      { __typename: 'CognitionEvaluationChatInputMessage' },
      { __typename: 'CognitionEvaluationDSLGenerationInputMessage' },
      userMessage => (
        <CognitionChatInputMessage
          message={userMessage as CognitionEvaluationChatInputMessage}
          index={index}
          isVisible={isVisible}
          elmId={elmId}
        />
      )
    )
    .with(
      { __typename: 'CognitionEvaluationChatOutputMessage' },
      modelMessage => (
        <CognitionChatOutputMessage
          message={modelMessage as CognitionEvaluationChatOutputMessage}
          index={index}
          onCopy={onCopy}
          onAction={onAction}
          components={components}
          showAssistantLabel={showAssistantLabel}
          isVisible={isVisible}
          elmId={elmId}
        />
      )
    )

    .otherwise(otherMessage => (
      <CognitionObjectMessage
        message={otherMessage}
        index={index}
        isVisible={isVisible}
        elmId={elmId}
      />
    ))
}

export default CognitionMessage
